import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { UserDialog } from '@/components/features/users/UserDialog';
import { UserApiKeyDialog } from '@/components/features/users/UserApiKeyDialog';
import { ReplaceAddressDialog } from '@/components/features/users/ReplaceAddressDialog';
import { CopyButton } from '@/components/shared/CopyButton';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { LoadingState } from '@/components/shared/LoadingState';
import { ErrorState } from '@/components/shared/ErrorState';
import { useUserDetail, useDeleteUser } from '@/lib/hooks';
import { useToast } from '@/lib/hooks/useToast';
import { formatDate } from '@/utils/formatters';
import { maskApiKey } from '@/utils/crypto';
import { ROUTES } from '@/config/routes';
import { Edit, Key, RefreshCw, Trash2, Wallet } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

export const Route = createFileRoute('/_auth/_admin/admin/users/$id')({
  component: UserDetailComponent,
});

function UserDetailComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [replaceAddressDialogOpen, setReplaceAddressDialogOpen] = useState(false);

  const { data, isLoading, isError, error, refetch } = useUserDetail(Number(id));
  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser();

  const breadcrumbs = [
    { label: 'Пользователи', href: ROUTES.ADMIN.USERS },
    { label: data?.name || `#${id}` },
  ];

  const handleDelete = () => {
    if (window.confirm('Вы уверены, что хотите деактивировать этого пользователя?')) {
      deleteUser(Number(id), {
        onSuccess: () => {
          success('Пользователь успешно деактивирован');
          navigate({ to: ROUTES.ADMIN.USERS });
        },
        onError: (err) => {
          toastError('Не удалось деактивировать пользователя', err.message);
        },
      });
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return <ErrorState title={error.name} message={error.message} onRetry={refetch} />;
  }

  const user = data!;

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbs} />
      
      <PageHeader
        title={user.name}
        description={`Пользователь #${user.id}`}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Редактировать
            </Button>
            <Button variant="outline" onClick={() => setApiKeyDialogOpen(true)}>
              <Key className="mr-2 h-4 w-4" />
              Сбросить API ключ
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Деактивировать
            </Button>
          </div>
        }
      />

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Информация о пользователе</CardTitle>
            <CardDescription>Личные данные и статус аккаунта</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Имя</Label>
                <Input value={user.name} disabled />
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user.email} disabled />
              </div>

              <div className="space-y-2">
                <Label>Роль</Label>
                <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                  {user.role === 'admin' ? 'Администратор' : 'Клиент'}
                </Badge>
              </div>

              <div className="space-y-2">
                <Label>Статус</Label>
                <Badge variant={user.is_active ? 'default' : 'destructive'}>
                  {user.is_active ? 'Активен' : 'Неактивен'}
                </Badge>
              </div>

              <div className="space-y-2">
                <Label>Дата создания</Label>
                <Input value={formatDate(user.created_at, 'full')} disabled />
              </div>

              {user.last_login_at && (
                <div className="space-y-2">
                  <Label>Последний вход</Label>
                  <Input value={formatDate(user.last_login_at, 'full')} disabled />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              <CardTitle>Финансовая информация</CardTitle>
            </div>
            <CardDescription>Баланс и комиссии пользователя</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Баланс</Label>
                <Input 
                  value={`${user.balance} USDT`}
                  disabled 
                  className="font-semibold"
                />
              </div>

              <div className="space-y-2">
                <Label>Комиссия на депозиты</Label>
                <Input 
                  value={user.deposit_commission}
                  disabled 
                />
              </div>

              <div className="space-y-2">
                <Label>Комиссия на выводы</Label>
                <Input 
                  value={user.withdrawal_commission}
                  disabled 
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>API учётные данные</CardTitle>
            <CardDescription>Интеграционные данные для доступа к API</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>API ключ</Label>
              <div className="flex gap-2">
                <Input value={maskApiKey(user.api_key || '')} disabled />
                {user.api_key && <CopyButton text={user.api_key} />}
              </div>
            </div>

            {user.webhook_url && (
              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <div className="flex gap-2">
                  <Input value={user.webhook_url} disabled />
                  <CopyButton text={user.webhook_url} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle>Адрес депозита</CardTitle>
                <CardDescription>
                  Постоянный статичный адрес пользователя для пополнений
                </CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={() => setReplaceAddressDialogOpen(true)}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Заменить адрес
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {user.static_wallet ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted p-3">
                <code className="flex-1 break-all font-mono text-sm">
                  {user.static_wallet.address}
                </code>
                <CopyButton text={user.static_wallet.address} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Адрес ещё не назначен.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <UserDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        mode="edit"
        user={user}
      />

      <UserApiKeyDialog
        open={apiKeyDialogOpen}
        onOpenChange={setApiKeyDialogOpen}
        userId={user.id}
      />

      <ReplaceAddressDialog
        open={replaceAddressDialogOpen}
        onOpenChange={setReplaceAddressDialogOpen}
        userId={user.id}
        currentAddress={user.static_wallet?.address ?? null}
      />
    </div>
  );
}
